import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import theme from 'stories/utils/theme'

const ShimmerPlaceholder = styled.div<{
    width: number
    height: number
    isDarkTheme?: boolean
}>`
    width: ${(props) => props.width}px;
    height: ${(props) => props.height}px;
    background-color: ${theme.colors.lightGrayBackground};
    border-radius: ${theme.borderRadius.normal};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    border: ${theme.colors.lightGrayBackground};
    position: relative;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, ${theme.colors.lightGrayBackground}, transparent);
        animation: shimmer 1.5s infinite;
    }

    @keyframes shimmer {
        0% {
            left: -100%;
        }
        100% {
            left: 100%;
        }
    }
`

interface ProgressiveImageLoadingOptions {
    width: number
    height: number
    isDarkTheme?: boolean
}

const useProgressiveImageLoading = (totalImages: number, rootMargin = '100px') => {
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0])) // Always load first image
    const imageRefs = useRef<(HTMLDivElement | null)[]>(new Array(totalImages).fill(null))
    const observerRef = useRef<IntersectionObserver | null>(null)

    useEffect(() => {
        // Clean up previous observer
        if (observerRef.current) {
            observerRef.current.disconnect()
        }

        // Don't create observer if no images or all images are already loaded
        if (totalImages <= 1 || loadedImages.size >= totalImages) {
            return
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const indicesToLoad: number[] = []

                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = parseInt(entry.target.getAttribute('data-image-index') || '0', 10)
                        if (!loadedImages.has(index)) {
                            indicesToLoad.push(index)
                        }
                        observerRef.current?.unobserve(entry.target) // Stop observing once triggered
                    }
                })

                if (indicesToLoad.length > 0) {
                    setLoadedImages((prev) => {
                        const newSet = new Set(prev)
                        indicesToLoad.forEach((index) => newSet.add(index))
                        return newSet
                    })
                }
            },
            {
                rootMargin,
                threshold: 0.1,
            }
        )

        // Observe all unloaded image containers
        imageRefs.current.forEach((ref, index) => {
            if (ref && !loadedImages.has(index) && observerRef.current) {
                observerRef.current.observe(ref)
            }
        })

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
                observerRef.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalImages, rootMargin, loadedImages.size]) // Re-run when loadedImages changes

    const setImageRef = (index: number) => (el: HTMLDivElement | null) => {
        imageRefs.current[index] = el

        // If observer exists and this image isn't loaded, start observing
        if (el && observerRef.current && !loadedImages.has(index)) {
            observerRef.current.observe(el)
        }
    }

    const shouldLoadImage = (index: number) => loadedImages.has(index)

    const createPlaceholder = (options: ProgressiveImageLoadingOptions) => {
        return <ShimmerPlaceholder {...options} />
    }

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
        }
    }, [])

    return {
        shouldLoadImage,
        setImageRef,
        loadedImagesCount: loadedImages.size,
        createPlaceholder,
    }
}

export default useProgressiveImageLoading
